/* One live set of controls: moving nodes keeps native forms and city-filter listeners intact. */
(() => {
  'use strict';
  let active;
  class MobileCollection {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-collection-sheet]');
      this.bar = root.querySelector('[data-collection-mobile-bar]');
      if (!this.dialog?.showModal || !this.bar) return;
      this.media = window.matchMedia('(max-width: 749px)');
      this.sidebar = root.querySelector('.collection-sidebar');
      this.nativeSort = root.querySelector('.directory-sort');
      this.dynamicSort = root.querySelector('[data-city-sort]');
      this.native = root.querySelector('[data-city-native]');
      this.panel = root.querySelector('[data-city-panel]');
      this.filterHost = root.querySelector('[data-mobile-filter-host]');
      this.sortHost = root.querySelector('[data-mobile-sort-host]');
      this.nativeHost = root.querySelector('[data-mobile-native-sort]');
      this.dynamicHost = root.querySelector('[data-mobile-dynamic-sort]');
      this.notice = root.querySelector('[data-mobile-sort-notice]');
      this.applyButton = root.querySelector('[data-collection-apply]');
      this.title = root.querySelector('[data-collection-sheet-title]');
      this.moves = [];
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.bar.addEventListener('click', (event) => {
        const button = event.target.closest('[data-collection-open]');
        if (button) this.open(button.dataset.collectionOpen, button);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-collection-close]')) this.close();
        if (event.target === this.dialog) {
          const bounds = this.dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
        }
      }, options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.restore(), options);
      this.dialog.addEventListener('submit', () => this.close(), options);
      this.applyButton.addEventListener('click', () => this.apply(), options);
      this.onResize = () => {
        if (!this.media.matches) this.close();
        document.body.classList.toggle('collection-mobile-controls', this.media.matches);
      };
      this.media.addEventListener('change', this.onResize);
      this.observer = new MutationObserver(() => this.syncSort());
      [this.native, this.panel, this.dynamicSort].filter(Boolean).forEach((node) => this.observer.observe(node, { attributes: true, attributeFilter: ['hidden', 'aria-busy'] }));
      root.classList.add('collection-mobile-ready');
      this.bar.hidden = false;
      this.onResize();
      document.documentElement.classList.remove('collection-mobile-pending');
      window.clearTimeout(window.rhCollectionPendingTimer);
    }
    move(node, host) {
      if (!node) return;
      const marker = document.createComment('Collection control position');
      node.before(marker);
      this.moves.push([node, marker]);
      host.append(node);
    }
    open(mode, opener) {
      if (!this.media.matches || this.dialog.open) return;
      this.mode = mode;
      this.opener = opener;
      this.title.textContent = mode === 'filters' ? 'Filter places' : 'Sort places';
      this.applyButton.textContent = mode === 'filters' ? 'Apply filters' : 'Apply sort';
      this.filterHost.hidden = mode !== 'filters';
      this.sortHost.hidden = mode !== 'sort';
      if (mode === 'filters') {
        this.nativeSnapshot = this.nativeValues();
        this.move(this.sidebar, this.filterHost);
        const details = this.sidebar?.querySelector('.collection-sidebar__details');
        if (details) { this.sidebarWasOpen = details.open; details.open = true; }
      } else {
        this.move(this.nativeSort, this.nativeHost);
        this.move(this.dynamicSort, this.dynamicHost);
      }
      this.syncSort();
      this.dialog.showModal();
      document.documentElement.classList.add('collection-sheet-open');
      const initial = mode === 'sort'
        ? this.sortHost.querySelector('[data-mobile-native-sort]:not([hidden]) select, [data-mobile-dynamic-sort]:not([hidden]) [data-city-sort]:not([hidden]) select')
        : this.filterHost.querySelector('input:not([type="hidden"])');
      (initial || this.dialog.querySelector('[data-collection-close]')).focus({ preventScroll: true });
    }
    syncSort() {
      if (this.mode !== 'sort') return;
      const dynamic = this.native?.hidden === true;
      this.nativeHost.hidden = dynamic;
      this.dynamicHost.hidden = !dynamic;
      const unavailable = dynamic ? !this.dynamicSort || this.dynamicSort.hidden : !this.nativeSort;
      this.notice.hidden = !unavailable;
      this.applyButton.textContent = unavailable ? 'Done' : 'Apply sort';
    }
    nativeValues() {
      const form = this.root.querySelector('[data-native-filter-form]');
      if (!form) return '';
      return JSON.stringify([...new FormData(form)].filter(([name]) => name.startsWith('filter.')));
    }
    apply() {
      const nativeForm = this.root.querySelector('[data-native-filter-form]');
      if (this.mode === 'sort') {
        const isNative = !this.native?.hidden;
        this.close();
        if (isNative && nativeForm && this.nativeSort) nativeForm.requestSubmit();
        return;
      }
      const refineForm = this.sidebar?.querySelector('[data-city-finder-form]');
      const values = refineForm ? new FormData(refineForm) : null;
      if (nativeForm && this.nativeSnapshot !== this.nativeValues()) {
        // Native facets require Shopify's server response; include any city edits in the same navigation.
        const current = new URL(window.location.href);
        const destination = new URL(nativeForm.action, current.origin);
        current.searchParams.forEach((value, name) => {
          if ((name.startsWith('rh_') && name !== 'rh_page') || name === 'preview_theme_id') destination.searchParams.append(name, value);
        });
        for (const [name, value] of new FormData(nativeForm)) {
          if ((name.startsWith('filter.') && !name.endsWith('.availability')) || name === 'sort_by') destination.searchParams.append(name, value);
        }
        ['rh_city', 'rh_q'].forEach((name) => {
          if (!values) return;
          const value = String(values.get(name) || '').trim();
          if (value) destination.searchParams.set(name, value); else destination.searchParams.delete(name);
        });
        this.close();
        window.location.assign(destination.href);
        return;
      }
      this.close();
      if (refineForm && values) {
        const params = new URLSearchParams(window.location.search);
        if (['rh_city', 'rh_q'].some((name) => String(values.get(name) || '').trim() !== (params.get(name) || ''))) refineForm.requestSubmit();
      }
    }
    restore() {
      const details = this.sidebar?.querySelector('.collection-sidebar__details');
      if (this.mode === 'filters' && details) details.open = this.sidebarWasOpen;
      this.moves.forEach(([node, marker]) => marker.replaceWith(node));
      this.moves = [];
      this.mode = '';
      document.documentElement.classList.remove('collection-sheet-open');
      if (this.media.matches && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }
    close() {
      if (this.dialog.open) this.dialog.close();
      this.restore();
    }
    dispose() {
      this.close();
      this.controller?.abort();
      this.observer?.disconnect();
      this.media?.removeEventListener('change', this.onResize);
      this.root.classList.remove('collection-mobile-ready');
      document.body.classList.remove('collection-mobile-controls');
    }
  }
  function initialize() {
    const root = document.querySelector('[data-city-results]');
    if (active?.root === root) return;
    active?.dispose();
    active = root ? new MobileCollection(root) : null;
  }
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    if (active && event.target.contains(active.root)) { active.dispose(); active = null; }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
